import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import BryntumContext from '../context/bryntumContext.jsx';
import AuthContext from '../context/authContext.jsx';

export const formatBoardData = (record) => ({
  id: record.id,
  name: record.name,
  title: record.name, // Add title for compatibility
  isWorkforceActive: record.isWorkforceActive,
  workforceActive: record.isWorkforceActive, // Add both for compatibility
  adminEmails: record.adminEmails || [],
  roleNames: record.roleNames,
  updater: record.updater,
  lastUpdatedAt: record.lastUpdatedAt,
  updatedBy: record.updater || 'N/A',
  createdAt: record.createdAt || 'N/A',
  age: record.createdAt ? `${Math.floor((Date.now() - new Date(record.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days` : '0 days',
  grants: record.grants,
  isSandbox: record.isSandbox,
  deleteDate: record.deleteDate,
  deletedBy: record.deletedBy,
  slug: record.slug,
  memberCount: record.adminEmails?.length || 0,
  role: record.roleNames?.[0],
  roles: record.roles,
  creator: record.creator,
  creatorId: record.creatorId,
  boardType: 'created'
})

export const useBoardData = () => {
  const { boardManager } = useContext(BryntumContext);
  const { user } = useContext(AuthContext);
  const [boards, setBoards] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const updateBoards = useCallback(() => {
    if (!boardManager?.boardNameStore) return;
    const boardStore = boardManager.boardNameStore;
    const boardData = boardStore.records.map(record => formatBoardData(record));
    setBoards(boardData);
    setRecentlyViewed(recentlyViewedBoards(boardData))
    setIsLoading(false);
  }, [boardManager]);

  const recentlyViewedBoards = (boardsRecord) => {
    const stored = localStorage.getItem("recentlyViewedBoards");
    if (!stored) return [];

    const recentBoards = JSON.parse(stored);
    const boardMap = new Map(boardsRecord.map((board) => [board.id, board]));

    return recentBoards
      .filter((recent) => boardMap.has(recent.id))
      .map((recent) => ({
        id: recent.id,
        type: "board",
        boardData: boardMap.get(recent.id),
        accessedAt: recent.accessedAt
      })).slice(0, 4)
  }

  const updateRecentlyViewed = useCallback((boardId) => {
    const stored = localStorage.getItem("recentlyViewedBoards");
    const recentBoards = stored ? JSON.parse(stored) : [];

    const updatedBoards = [
      { id: boardId, accessedAt: new Date().toISOString() },
      ...recentBoards.filter((board) => board.id !== boardId)
    ].slice(0, 4);

    localStorage.setItem("recentlyViewedBoards", JSON.stringify(updatedBoards));
  }, []);

  const categorizedBoards = useMemo(() => {
    if (!boards.length || !user) return { created: [], invited: [] };

    const created = boards.filter((board) => board.creatorId === user.id);

    // Boards where user is invited (not creator)
    const invited = boards.filter((board) => board.creatorId !== user.id);

    return { created, invited };
  }, [boards, user]);

  useEffect(() => {
    if (!boardManager?.boardNameStore) return;
    const boardStore = boardManager.boardNameStore;

    updateBoards();
    
    const detacher = boardStore.on({
      add: updateBoards,
      update: updateBoards,
      remove: updateBoards,
      refresh: updateBoards,
      idChange: updateBoards,
      change: updateBoards,
      dataChange: updateBoards,
      swivuAddBoardFailed: updateBoards
    });
    
    return detacher;
  }, [boardManager, updateBoards]);

  return { isLoading, categorizedBoards, recentlyViewed, updateRecentlyViewed };
}; 